// Parses news body with inline image markers: ![alt](uuid)
// Consecutive images (separated by whitespace only) are grouped into a gallery grid.

const IMAGE_MARKER = /!\[([^\]]*)\]\(([^)]+)\)/g;

export interface BodyRendererOptions {
  onImageClick?: (uuid: string, allUuids: string[]) => void;
}

type Token =
  | { type: 'text'; value: string }
  | { type: 'image'; uuid: string; alt: string };

type Group =
  | { type: 'text'; value: string }
  | { type: 'images'; images: Array<{ uuid: string; alt: string }> };

function tokenize(text: string): Token[] {
  const tokens: Token[] = [];
  let lastIndex = 0;
  IMAGE_MARKER.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = IMAGE_MARKER.exec(text)) !== null) {
    tokens.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    tokens.push({ type: 'image', uuid: match[2] ?? '', alt: match[1] ?? '' });
    lastIndex = match.index + match[0].length;
  }
  tokens.push({ type: 'text', value: text.slice(lastIndex) });
  return tokens;
}

function groupTokens(tokens: Token[]): Group[] {
  const groups: Group[] = [];
  let i = 0;

  while (i < tokens.length) {
    const t = tokens[i]!;

    if (t.type === 'text') {
      if (t.value.trim()) groups.push({ type: 'text', value: t.value });
      i++;
    } else {
      // Collect consecutive images (whitespace-only text between them is bridged)
      const images: Array<{ uuid: string; alt: string }> = [{ uuid: t.uuid, alt: t.alt }];
      i++;

      while (i < tokens.length) {
        const next = tokens[i]!;
        if (next.type === 'image') {
          images.push({ uuid: next.uuid, alt: next.alt });
          i++;
        } else if (next.type === 'text' && !next.value.trim()) {
          // Whitespace gap — bridge only if the token after is another image
          if (i + 1 < tokens.length && tokens[i + 1]!.type === 'image') {
            i++; // skip whitespace
          } else {
            break;
          }
        } else {
          break;
        }
      }

      groups.push({ type: 'images', images });
    }
  }

  return groups;
}

export function renderBody(text: string, options: BodyRendererOptions = {}): DocumentFragment {
  const tokens = tokenize(text);
  const groups = groupTokens(tokens);

  // Collect all image UUIDs for lightbox navigation
  const allUuids: string[] = groups
    .filter((g): g is Extract<Group, { type: 'images' }> => g.type === 'images')
    .flatMap(g => g.images.map(img => img.uuid));

  const fragment = document.createDocumentFragment();

  for (const group of groups) {
    if (group.type === 'text') {
      fragment.appendChild(createTextBlock(group.value));
    } else if (group.images.length === 1) {
      fragment.appendChild(createSingleImage(group.images[0]!.uuid, group.images[0]!.alt, allUuids, options));
    } else {
      fragment.appendChild(createGallery(group.images, allUuids, options));
    }
  }

  return fragment;
}

function createTextBlock(text: string): DocumentFragment {
  const frag = document.createDocumentFragment();
  const lines = text.split('\n');
  const nonEmpty = lines.map(l => l.trim()).filter(Boolean);
  for (const line of nonEmpty) {
    const p = document.createElement('p');
    p.className = 'detail-body__para';
    p.appendChild(document.createTextNode(line));
    frag.appendChild(p);
  }
  return frag;
}

function createSingleImage(uuid: string, alt: string, allUuids: string[], options: BodyRendererOptions): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'detail-body__inline-image';

  wrapper.appendChild(buildPicture(uuid, 'original', alt));

  if (options.onImageClick) {
    wrapper.style.cursor = 'pointer';
    wrapper.addEventListener('click', () => options.onImageClick!(uuid, allUuids));
  }

  return wrapper;
}

function createGallery(images: Array<{ uuid: string; alt: string }>, allUuids: string[], options: BodyRendererOptions): HTMLElement {
  const grid = document.createElement('div');
  grid.className = `detail-gallery detail-gallery--${images.length === 2 ? '2col' : images.length === 3 ? '3col' : 'grid'}`;

  for (const img of images) {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'detail-gallery__item';
    item.appendChild(buildPicture(img.uuid, 'medium', img.alt));

    if (options.onImageClick) {
      item.addEventListener('click', () => options.onImageClick!(img.uuid, allUuids));
    }

    grid.appendChild(item);
  }

  return grid;
}

function buildPicture(uuid: string, size: 'thumb' | 'medium' | 'original', alt: string): HTMLElement {
  const picture = document.createElement('picture');

  const avif = document.createElement('source');
  avif.srcset = `/uploads/news/${uuid}/${size}.avif`;
  avif.type = 'image/avif';
  picture.appendChild(avif);

  const webp = document.createElement('source');
  webp.srcset = `/uploads/news/${uuid}/${size}.webp`;
  webp.type = 'image/webp';
  picture.appendChild(webp);

  const img = document.createElement('img');
  img.src = `/uploads/news/${uuid}/${size}.jpg`;
  img.alt = alt;
  img.loading = 'lazy';
  picture.appendChild(img);

  return picture;
}
